import { List, Set } from 'immutable';
import every from 'lodash/every';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import { createAction } from 'typesafe-actions';

import { InfiniteScrollActions } from '../constants/InfiniteScroll';
import Logger from '../tools/Logger';

const log = Logger.child({ component: 'InfiniteScroll' });

const paramValidator: $TSFixMeFunction = (
  paramsObject,
  validator,
  logger,
  name,
  errorMessage,
): boolean =>
  every(paramsObject, (value, key) => {
    const isValidParam = validator(value);
    if (!isValidParam) {
      logger.error(`Invalid ${name} parameter: ${key}. ${errorMessage}`);
    }
    return isValidParam;
  });

interface OnRandomAccessInfiniteScrollParams {
  items: $TSFixMe;
  originalItems: $TSFixMe;
  firstVisibleIndex: $TSFixMe;
  lastVisibleIndex: $TSFixMe;
  dataFetcher: $TSFixMe;
  fetchingOffsets: $TSFixMe;
  apiLimit: $TSFixMe;
  totalCount: $TSFixMe;
  loadedPages: $TSFixMe;
}

type OnRandomAccessInfiniteScrollPayload = OnRandomAccessInfiniteScrollParams & {
  allParamsValid: boolean;
};

export const onRandomAccessInfiniteScroll = createAction(
  InfiniteScrollActions.ON_RANDOM_ACCESS_INFINITE_SCROLL,
  ({
    items,
    originalItems,
    firstVisibleIndex,
    lastVisibleIndex,
    dataFetcher,
    fetchingOffsets,
    apiLimit,
    totalCount,
    loadedPages,
  }: OnRandomAccessInfiniteScrollParams) => {
    // Parameter Validation
    let allParamsValid = false;
    // numbers
    allParamsValid = paramValidator(
      { firstVisibleIndex, lastVisibleIndex, apiLimit, totalCount },
      isNumber,
      log,
      'InfiniteScroll',
      'Expected a Number.',
    );
    // arrays
    allParamsValid =
      allParamsValid &&
      paramValidator(
        { items, originalItems, loadedPages },
        isArray,
        log,
        'InfiniteScroll',
        'Expected an Array.',
      );
    // Immutable Lists
    allParamsValid =
      allParamsValid &&
      paramValidator(
        { fetchingOffsets },
        (value: $TSFixMe) => List.isList(value) || Set.isSet(value),
        log,
        'InfiniteScroll',
        'Expected an Immutable List.',
      );
    // functions
    allParamsValid =
      allParamsValid &&
      paramValidator({ dataFetcher }, isFunction, log, 'InfiniteScroll', 'Expected a Function.');

    return {
      items,
      originalItems,
      firstVisibleIndex,
      lastVisibleIndex,
      dataFetcher,
      fetchingOffsets,
      apiLimit,
      totalCount,
      loadedPages,
      allParamsValid,
    };
  },
)<OnRandomAccessInfiniteScrollPayload>();
export type OnRandomAccessInfiniteScrollAction = ReturnType<typeof onRandomAccessInfiniteScroll>;

interface OnInfiniteScrollParams {
  items: $TSFixMe;
  dataFetcher: $TSFixMe;
  hasFetchedData: $TSFixMe;
  pageSize: $TSFixMe;
  listItemTotal: $TSFixMe;
  forceFetch: $TSFixMe;
}

type OnInfiniteScrollPayload = OnInfiniteScrollParams & { allParamsValid: boolean };

export const onInfiniteScroll = createAction(
  InfiniteScrollActions.ON_INFINITE_SCROLL,

  ({
    items,
    dataFetcher,
    hasFetchedData,
    pageSize,
    listItemTotal, // param that is only defined when there are no more items to fetch
    forceFetch,
  }: OnInfiniteScrollParams) => {
    // Parameter Validation
    let allParamsValid = false;
    // numbers
    allParamsValid = paramValidator(
      { pageSize },
      isNumber,
      log,
      'InfiniteScroll',
      'Expected a Number.',
    );
    // booleans
    allParamsValid =
      allParamsValid &&
      paramValidator(
        { hasFetchedData, forceFetch },
        isBoolean,
        log,
        'InfiniteScroll',
        'Expected a Boolean.',
      );
    // arrays
    allParamsValid =
      allParamsValid &&
      paramValidator({ items }, isArray, log, 'InfiniteScroll', 'Expected an Array.');
    // functions
    allParamsValid =
      allParamsValid &&
      paramValidator({ dataFetcher }, isFunction, log, 'InfiniteScroll', 'Expected a Function.');

    return {
      allParamsValid,
      items,
      dataFetcher,
      hasFetchedData,
      pageSize,
      listItemTotal,
      forceFetch,
    };
  },
)<OnInfiniteScrollPayload>();
export type OnInfiniteScrollAction = ReturnType<typeof onInfiniteScroll>;
